/**
 * useSubmitDeck Hook
 *
 * Handles the client-side submission flow for startup pitch decks:
 * - File reading + base64 encoding
 * - Calling the process-submission edge function (public, no auth)
 * - Progress tracking through stages
 * - Error handling with centralized error utilities
 */

import { useState, useCallback } from 'react';
import { Platform } from 'react-native';
import { handleError } from '@/lib/errors';

// ============================================================================
// Types
// ============================================================================

export type SubmissionStage =
  | 'idle'
  | 'reading-file'    // Reading file as base64
  | 'uploading'       // Sending to edge function
  | 'processing'      // Server-side scoring
  | 'complete'        // Success
  | 'already-rated'   // Duplicate of an already-scored submission — redirect to claim
  | 'error';          // Failed

export interface SubmissionFormData {
  companyName: string;
  contactEmail: string;
  contactName?: string;
  /** 400-char free-text context — "how did you find me / why are you reaching out". Phase 116.1. */
  submitterNote?: string;
  consentGiven: boolean;
  /** File URI from document picker */
  fileUri: string;
  /** File name */
  fileName: string;
  /** The investor's slug */
  slug: string;
  /** Turnstile CAPTCHA token (added in Plan 03) */
  captchaToken?: string;
}

export interface SubmissionResult {
  success: boolean;
  submissionId?: string;
  message?: string;
  error?: string;
  /** Set when the server rejects a duplicate submission already scored. */
  code?: 'already_rated';
  /** Where to send the submitter to claim their existing company profile. */
  claimUrl?: string;
}

export interface UseSubmitDeckReturn {
  submit: (data: SubmissionFormData) => Promise<SubmissionResult>;
  stage: SubmissionStage;
  progress: number;     // 0-100
  error: string | null;
  reset: () => void;
}

// ============================================================================
// File Reading Utility
// ============================================================================

/**
 * Read a file as base64 string (works on both web and native).
 * Reuses the same pattern from pitch-deck-analysis.ts.
 */
async function readFileAsBase64(fileUri: string): Promise<string> {
  if (Platform.OS === 'web') {
    const response = await fetch(fileUri);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } else {
    const { readAsStringAsync } = await import('expo-file-system/legacy');
    return readAsStringAsync(fileUri, { encoding: 'base64' });
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useSubmitDeck(): UseSubmitDeckReturn {
  const [stage, setStage] = useState<SubmissionStage>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (data: SubmissionFormData): Promise<SubmissionResult> => {
    setStage('reading-file');
    setProgress(5);
    setError(null);

    try {
      // Step 1: Read file as base64 (5-20%)
      let deckBase64: string;
      try {
        deckBase64 = await readFileAsBase64(data.fileUri);
        setProgress(20);
      } catch {
        throw new Error('Failed to read the file. Please try again.');
      }

      // Validate file size (base64 is ~33% larger than binary)
      const estimatedSizeBytes = (deckBase64.length * 3) / 4;
      if (estimatedSizeBytes > 20 * 1024 * 1024) {
        throw new Error('File is too large. Maximum size is 20MB.');
      }

      // Step 2: Upload to edge function (20-80%)
      setStage('uploading');
      setProgress(25);

      // Call process-submission edge function
      // This is a public endpoint -- no auth required
      // We call it directly via fetch (not callEdgeFunction) since
      // callEdgeFunction adds auth headers which aren't needed
      const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
      const anonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !anonKey) {
        throw new Error('Configuration error. Please try again later.');
      }

      setProgress(30);

      const response = await fetch(
        `${supabaseUrl}/functions/v1/process-submission`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': anonKey,
          },
          body: JSON.stringify({
            slug: data.slug,
            companyName: data.companyName,
            contactEmail: data.contactEmail,
            contactName: data.contactName || undefined,
            submitterNote: data.submitterNote?.trim() || undefined,
            consentGiven: data.consentGiven,
            deckBase64,
            deckFileName: data.fileName,
            captchaToken: data.captchaToken || undefined,
          }),
        }
      );

      setProgress(70);
      setStage('processing');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Phase 116.1 — Option A: server signals an already-rated duplicate.
        // Surface it as a distinct stage so the UI can redirect to the claim
        // flow instead of rendering a generic error.
        if (response.status === 409 && errorData?.code === 'already_rated') {
          const claimMessage =
            errorData.message ||
            'This company has already been submitted and scored. Visit Inverio to claim your profile.';
          setProgress(100);
          setStage('already-rated');
          setError(null);
          return {
            success: false,
            code: 'already_rated',
            claimUrl: errorData.claimUrl,
            message: claimMessage,
          };
        }

        const errorMessage = errorData.error || `Submission failed (${response.status})`;
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setProgress(100);
      setStage('complete');

      return {
        success: true,
        submissionId: result.submissionId,
        message: result.message || 'Deck submitted successfully!',
      };
    } catch (err) {
      const { message, isCancelled } = handleError(err, {
        context: 'useSubmitDeck.submit',
      });
      if (!isCancelled) {
        setError(message);
        setStage('error');
      }
      return { success: false, error: message };
    }
  }, []);

  const reset = useCallback(() => {
    setStage('idle');
    setProgress(0);
    setError(null);
  }, []);

  return { submit, stage, progress, error, reset };
}
